import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Header } from './components/Header';
import { EnterpriseShell } from './components/layout/EnterpriseShell';
import { BannerNotifications } from './components/BannerNotifications';
import { KpiGrid } from './components/KpiGrid';
import { ChartsSection } from './components/ChartsSection';
import { ThreatIntelSection } from './components/ThreatIntelSection';
import { MastersTab } from './components/MastersTab';
import { IncidentsTab } from './components/IncidentsTab';
import { CasesPanel } from './components/CasesPanel';
import { DataLakeTab } from './components/DataLakeTab';
import { IOCUploadTab } from './components/IOCUploadTab';
import { LogsTab } from './components/LogsTab';
import { LogMasterTab } from './components/LogMasterTab';
import { ReportsTab } from './components/ReportsTab';
import { SettingsPanel } from './components/SettingsPanel';
import { MsspOverviewTab } from './components/MsspOverviewTab';
import { CoverageTab } from './components/CoverageTab';
import { OobCoverageAssessmentTab } from './components/OobCoverageAssessmentTab';
import { CustomAnalyticsStudioTab } from './components/CustomAnalyticsStudioTab';
import { HuntsTab } from './components/HuntsTab';
import { NotebookOpsTab } from './components/NotebookOpsTab';
import { WorkbooksTab } from './components/WorkbooksTab';
import { ResponseOpsPlaybookStudioTab } from './components/ResponseOpsPlaybookStudioTab';
import { RiskCommandCenterTab } from './components/RiskCommandCenterTab';
import { MissionControlTab } from './components/MissionControlTab';
import { ForwardOperatingBaseTab } from './components/ForwardOperatingBaseTab';
import { MainOperatingBaseTab } from './components/MainOperatingBaseTab';
import { AiCommandControlCenterTab } from './components/AiCommandControlCenterTab';
import { MlInsightsEngineTab } from './components/MlInsightsEngineTab';
import { IncidentOpsAiSimulationLabTab } from './components/IncidentOpsAiSimulationLabTab';
import { AdvancedHuntingTab } from './components/AdvancedHuntingTab';
import { PhishingTab } from './components/PhishingTab';
import { DarkWebTab } from './components/DarkWebTab';
import { SpiderFootTab } from './components/SpiderFootTab';
import { DnstwistTab } from './components/DnstwistTab';
import { CustomerPortal } from './components/CustomerPortal';
import { SecurityCopilotTab } from './components/SecurityCopilotTab';
import { VulnerabilityManagementTab } from './components/VulnerabilityManagementTab';
import { Entity360Tab } from './components/Entity360Tab';
import { Entity360HuntOpsTab } from './components/Entity360HuntOpsTab';
import { SocDigitalTwinTab } from './components/SocDigitalTwinTab';
import { SocDebtDashboardTab } from './components/SocDebtDashboardTab';
import { AiReportBuilderTab } from './components/AiReportBuilderTab';
import { ConnectorHealthGuardianTab } from './components/ConnectorHealthGuardianTab';
import { DetectionQualityScoreTab } from './components/DetectionQualityScoreTab';
import { SocKpiTab } from './components/SocKpiTab';
import { MttrWidget } from './components/MttrWidget';
import { type Incident, type Case } from './data/demoData';
import type { ClosePayload } from './components/CloseIncidentDialog';
import type { IncidentStatusPayload } from './components/IncidentStatusDialog';
import type { IncidentSeverityPayload } from './components/IncidentSeverityDialog';
import { calcMTTD, calcMTTR, calcNoiseRatio } from './lib/utils';
import { useLiveData } from './hooks/useLiveData';
import { useTriageSummary } from './hooks/useTriageSummary';
import { useIncidentWatcher, type NewIncidentEvent } from './hooks/useIncidentWatcher';
import { useCustomers } from './context/CustomerContext';
import { useAuth } from './context/AuthContext';
import { NAV_TAB_TO_PERMISSION, TAB_CAPABILITY_MAP } from './lib/permissionsCatalog';
import { normalizeDashboardTimeRange, type DashboardTimeRange } from './lib/timeRange';
import { toast } from './hooks/use-toast';
import { ToastAction } from './components/ui/toast';

// Tab definitions.
//   `adminOnly`        - only the admin role sees the tab (e.g. Logs).
//   `hideForRoles`     - explicit blacklist (e.g. SOC Manager doesn't see
//                        MSSP Overview because they manage one team within
//                        the MSSP, not the MSSP-wide cross-tenant view).
// The server still enforces every backing endpoint independently - the lists
// below are a UX nicety so users never see buttons that would 403.
type RoleId = 'developer' | 'admin' | 'software_tester' | 'soc_manager' | 'soc_l1' | 'soc_l2' | 'analyst' | 'customer_viewer';
const TABS: { id: string; label: string; adminOnly?: boolean; developerOnly?: boolean; hideForRoles?: RoleId[] }[] = [
  { id: 'mission_control', label: 'Mission Control' },
  { id: 'forward_operating_base', label: 'Forward Operating Base' },
  { id: 'main_operating_base', label: 'Main Operating Base' },
  { id: 'ai_command_control', label: 'AI Command & Control Center' },
  { id: 'ml_insights', label: 'ML Insights Engine' },
  { id: 'simulation_lab', label: 'IncidentOps → AI Simulation Lab' },
  { id: 'mssp', label: 'MSSP Overview', hideForRoles: ['soc_manager'] },
  { id: 'overview', label: 'Overview' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'hunts', label: 'HuntOps' },
  { id: 'workbooks', label: 'Workbooks' },
  { id: 'notebook_ops', label: 'NotebookOps' },
  { id: 'responseops', label: 'ResponseOps Studio' },
  { id: 'risk_command_center', label: 'Risk Command Center' },
  { id: 'advanced_hunting', label: 'Advanced Hunting' },
  { id: 'phishing', label: 'Phishing' },
  { id: 'darkweb', label: 'External Attack Surface' },
  { id: 'spiderfoot', label: 'OSINT Reconnaissance' },
  { id: 'dnstwist', label: 'Lookalike Domain Scanner' },
  { id: 'coverage', label: 'MITRE Coverage' },
  { id: 'oob_coverage_assessment', label: 'AnalyticsOps' },
  { id: 'custom_analytics_studio', label: 'Custom Analytics Studio' },
  { id: 'vulnerability_mgmt', label: 'Vulnerability Management' },
  { id: 'entity_360', label: 'Entity 360' },
  { id: 'soc_digital_twin', label: 'SOC Digital Twin' },
  { id: 'soc_debt', label: 'SOC Optimization' },
  { id: 'report_builder', label: 'Report Builder' },
  { id: 'connector_health', label: 'Data Connector Health' },
  { id: 'detection_quality', label: 'Detection Quality' },
  { id: 'customer_exec', label: 'SOC KPI' },
  { id: 'datalake', label: 'AI Assistant' },
  // Threat Intelligence: visible to admin + every SOC tier so L1/L2
  // can pull IOC reputation. The IOC upload form inside is still admin-only
  // (gated below at render time + by the /ioc/upload route).
  { id: 'ioc', label: 'Threat Intelligence' },
  { id: 'security_copilot', label: 'Security Copilot' },
  { id: 'reports', label: 'Reports' },
  { id: 'log_master', label: 'Audit Logs' },
  { id: 'masters', label: 'Administration', developerOnly: true },
  { id: 'logs', label: 'Operational Logs', adminOnly: true },
];


type ModuleNavLayout = 'hamburger' | 'horizontal';

const TAB_FEATURE_FLAGS: Record<string, string> = {
  mission_control: 'mission_control',
  forward_operating_base: 'forward_operating_base',
  main_operating_base: 'main_operating_base',
  ai_command_control: 'ai_command_control',
  ml_insights: 'ml_insights_engine',
  simulation_lab: 'ai_simulation_lab',
  hunts: 'active_threat_hunting',
  workbooks: 'huntops_workbooks',
  notebook_ops: 'notebook_ops',
  responseops: 'responseops_playbook_studio',
  risk_command_center: 'risk_command_center',
  advanced_hunting: 'advanced_hunting',
  datalake: 'ai_assistant',
  darkweb: 'darkweb_scan',
  spiderfoot: 'spiderfoot_scan',
  dnstwist: 'dnstwist_scan',
  coverage: 'mitre_defend_coverage',
  oob_coverage_assessment: 'oob_coverage_assessment',
  custom_analytics_studio: 'custom_analytics_studio',
  ioc: 'threat_intel_lookup',
  security_copilot: 'security_copilot',
  vulnerability_mgmt: 'vulnerability_mgmt',
  entity_360: 'entity_360',
  soc_digital_twin: 'soc_digital_twin',
  soc_debt: 'soc_debt_dashboard',
  report_builder: 'ai_report_builder',
  connector_health: 'connector_health_guardian',
  detection_quality: 'detection_quality_score',
  log_master: 'log_master',
  customer_exec: 'customer_executive_portal',
};

export default function App() {
  const { user, canSee, canWrite, canCapability } = useAuth();
  // Customer-portal users (single-tenant scoped, read-only) get a completely
  // different shell - the MSSP-wide nav is irrelevant and the underlying APIs
  // would 403 anyway. Render the lightweight portal layout instead.
  if (user?.role === 'customer_viewer') {
    return <CustomerPortal />;
  }
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const s = localStorage.getItem('soc-darkmode');
    return s !== null ? s === 'true' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'overview';
    const qs = new URLSearchParams(window.location.search);
    // ?incident=… deep-links always force the Incidents tab so the drawer can
    // open over it (regardless of any conflicting ?tab=… in the URL).
    if (qs.get('incident')) return 'incidents';
    const requested = qs.get('tab');
    if (requested && TABS.some(t => t.id === requested)) return requested;
    return 'overview';
  });
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
useEffect(() => {
  let cancelled = false;

  async function loadFeatureFlags() {
    try {
      const resp = await fetch('/api/masters/feature-flags', {
        credentials: 'include',
      });

      if (!resp.ok) return;

      const data = await resp.json() as { flags?: Record<string, boolean> };

      if (!cancelled) {
        setFeatureFlags(data.flags ?? {});
      }
    } catch {
      if (!cancelled) {
        setFeatureFlags({});
      }
    }
  }

  if (user) {
    void loadFeatureFlags();
  }

  return () => {
    cancelled = true;
  };
}, [user?.id]);
  const [dateFilter, setDateFilter] = useState<DashboardTimeRange>(() => {
    if (typeof window === 'undefined') return '24h';
    return normalizeDashboardTimeRange(localStorage.getItem('soc-dashboard-time-range'));
  });
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const [moduleNavLayout, setModuleNavLayout] = useState<ModuleNavLayout>(() => {
    if (typeof window === 'undefined') return 'hamburger';
    return localStorage.getItem('soc-module-nav-layout') === 'horizontal' ? 'horizontal' : 'hamburger';
  });
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [pendingKql, setPendingKql] = useState<string | null>(null);
  const [autoOpenIncident, setAutoOpenIncident] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('incident');
  });
  const [pendingCustomerParam, setPendingCustomerParam] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('customer');
  });

  function runKqlInConsole(query: string) {
    // Incident evidence, entity pivots and inline KQL snippets should land in
    // Advanced Hunting, not the legacy AI Assistant console. The AH tab
    // has its own run_query/backend enforcement, so this client-side check only
    // prevents a confusing tab switch for roles without hunting access.
    if (!canCapability('advanced_hunting', 'set_query') && !canCapability('advanced_hunting', 'run_query')) {
      const { dismiss } = toast({
        title: 'Permission denied',
        description: 'Your role is not allowed to set Advanced Hunting queries from pivots.',
        variant: 'destructive',
      });
      return;
    }
    setPendingKql(query);
    setActiveTab('advanced_hunting');
  }

  // ── Customer state from shared context ──
  const { customers, activeCustomerId, activeCustomer, setActiveCustomerId, refresh: refreshCustomers } = useCustomers();

  // Tabs are filtered by:
  //   1. `adminOnly`        - admin-tier features (e.g. global IOC feed)
  //   2. per-customer matrix - for SOC users with explicit per-tab permissions
  //                             on the active customer; admins/legacy users
  //                             pass through (canRead returns true).
  // The "default" workspace (no customer selected) skips the matrix check -
  // analysts always see the MSSP-wide nav even before picking a customer.
  const visibleTabs = TABS.filter((t) => {
  const role = user?.role;

  const featureKey = TAB_FEATURE_FLAGS[t.id];
  if (featureKey && featureFlags[featureKey] === false) return false;

  const capabilityGate = TAB_CAPABILITY_MAP[t.id];
  if (capabilityGate && !canCapability(capabilityGate.moduleId, capabilityGate.capability)) return false;

  if (t.developerOnly && role !== 'developer') return false;

  if (t.adminOnly && role !== 'admin' && role !== 'developer') return false;

  if (t.hideForRoles && role && t.hideForRoles.includes(role as RoleId)) return false;

  if (role === 'developer') return true;

  const permTab = NAV_TAB_TO_PERMISSION[t.id];
  if (!permTab) return true;
  if (!activeCustomerId || activeCustomerId === 'default') return true;

  return canSee(permTab, activeCustomerId);
});

  // Dashboard Settings remains available only to Developer and Admin.
  // Developer receives full platform configuration. Admin receives a reduced
  // SOC administration view (customers, users, audit, SLA and suppression).
  // SOC Manager/L1/L2/Customer Viewer do not see the gear icon.
  const canOpenSettings = (user?.role === 'admin' || user?.role === 'developer') && canCapability('settings', 'visible');

  // Defence in depth for deep-links: if the URL specifies a tab the user
  // can't see in the nav (e.g. ?tab=logs for soc_l2, or ?tab=mssp for
  // soc_manager), bounce them back to overview. The backend already 403s the
  // underlying APIs, but we don't want a hidden tab rendered just because the
  // querystring asked for it.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(t => t.id === activeTab)) {
      setActiveTab('overview');
    }
  }, [visibleTabs, activeTab]);

  // If the dashboard fetch reports that the active customerId no longer exists
  // (e.g. it was deleted in another tab/session), reset to the default workspace
  // and re-pull the customer list so the dropdown is consistent.
  const handleCustomerNotFound = useCallback(() => {
    setActiveCustomerId('default');
    refreshCustomers();
  }, [setActiveCustomerId, refreshCustomers]);

  const {
    incidents: liveIncidents,
    dailyAlerts,
    threatArticles,
    secureScore,
    source,
    isLoading,
    error: liveError,
    customerError,
    lastRefresh,
    refresh,
    azureConfigured,
  } = useLiveData(30, activeCustomerId !== 'default' ? activeCustomerId : undefined, dateFilter, handleCustomerNotFound);

  const [incidents, setIncidents] = useState<Incident[]>(liveIncidents);
  useEffect(() => { setIncidents(liveIncidents); }, [liveIncidents]);

  // ── Global new-incident watcher (toast popup across all customers) ──
  // Polls a slim per-customer endpoint every 30s. The first poll seeds a
  // baseline silently; subsequent polls fire one toast per truly-new
  // incident, with an "Open" action that switches active customer + jumps
  // straight to that incident.
  const incidentDismissRef = useRef<Map<string, () => void>>(new Map());

  const handleNewIncident = useCallback((ev: NewIncidentEvent) => {
    const sevColor =
      ev.incident.severity === 'High' ? '#ef4444' :
      ev.incident.severity === 'Medium' ? '#f59e0b' :
      ev.incident.severity === 'Low' ? '#3b82f6' : '#6b7280';

    // SLA-safety: when the dashboard isn't the focused tab, the in-app toast
    // is invisible. Fire an OS-level notification (best-effort - only if the
    // analyst granted permission) so a Critical incident can't burn its
    // 5-minute TTA while the analyst is in email or Teams.
    if (ev.detectedWhileHidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const n = new Notification(`New ${ev.incident.severity} incident · ${ev.customerName}`, {
          body: ev.incident.title,
          tag: `kloudsecure-${ev.customerId}-${ev.incident.incidentNumber}`,
          requireInteraction: ev.incident.severity === 'High' || ev.incident.severity === 'Critical',
        });
        n.onclick = () => {
          window.focus();
          setActiveCustomerId(ev.customerId);
          setActiveTab('incidents');
          setAutoOpenIncident(ev.incident.incidentNumber);
          n.close();
        };
      } catch { /* notification blocked or unsupported */ }
    }

    const { dismiss } = toast({
      // Real anchor so the browser's native context menu offers
      // "Open link in new tab" / "Copy link" on right-click. target=_blank
      // also lets a plain left-click jump straight to the Defender portal,
      // while the in-app "Open" button (action below) handles the
      // customer-switch + drawer-open flow inside the dashboard.
      title: (
        <a
          href={ev.incident.webUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline focus:underline focus:outline-none"
          title="Open in Microsoft Defender (right-click for options)"
        >
          {`New ${ev.incident.severity} incident · ${ev.customerName}`}
        </a>
      ),
      description: ev.incident.title,
      duration: 12_000,
      style: { borderLeft: `4px solid ${sevColor}` },
      action: (
        <ToastAction altText="Open incident" onClick={() => {
          // Always switch (idempotent in CustomerContext if same id) to avoid
          // a stale-closure bug: the user may have changed customer between
          // toast creation and click, so we can't trust a captured value.
          setActiveCustomerId(ev.customerId);
          setActiveTab('incidents');
          setAutoOpenIncident(ev.incident.incidentNumber);
        }}>
          Open
        </ToastAction>
      ),
    });
    incidentDismissRef.current.set(`${ev.customerId}::${ev.incident.incidentNumber}`, dismiss);
  }, [setActiveCustomerId]);

  const handleIncidentAcknowledged = useCallback((ev: { customerId: string; incidentNumber: string }) => {
    const key = `${ev.customerId}::${ev.incidentNumber}`;
    const dismiss = incidentDismissRef.current.get(key);
    if (dismiss) { dismiss(); incidentDismissRef.current.delete(key); }
  }, []);

  useIncidentWatcher({
    intervalSeconds: 30,
    enabled: azureConfigured,
    onNewIncident: handleNewIncident,
    onIncidentAcknowledged: handleIncidentAcknowledged,
  });

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('soc-darkmode', String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('soc-dashboard-time-range', dateFilter);
  }, [dateFilter]);

  useEffect(() => {
    localStorage.setItem('soc-module-nav-layout', moduleNavLayout);
    if (moduleNavLayout === 'horizontal') {
      setModuleMenuOpen(false);
    }
  }, [moduleNavLayout]);

  // Keep ?tab=… in the URL so deep-links (e.g. ?tab=reports) work and so a
  // refresh lands the user back on the same tab. The `overview` default is
  // omitted from the URL to keep the address bar clean.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get('tab');
    if (activeTab === 'overview') {
      if (current === null) return;
      url.searchParams.delete('tab');
    } else {
      if (current === activeTab) return;
      url.searchParams.set('tab', activeTab);
    }
    window.history.replaceState({}, '', url.toString());
  }, [activeTab]);

  // Apply ?customer=<id> from the URL once customers have loaded.
  useEffect(() => {
    if (!pendingCustomerParam) return;
    if (!customers || customers.length === 0) return;
    const exists = customers.some(c => c.id === pendingCustomerParam);
    if (exists && pendingCustomerParam !== activeCustomerId) {
      setActiveCustomerId(pendingCustomerParam);
    }
    setPendingCustomerParam(null);
  }, [pendingCustomerParam, customers, activeCustomerId, setActiveCustomerId]);

  const filteredIncidents = incidents; // API already returns the correct time window

  const highIncidents = filteredIncidents.filter(i => i.severity === 'High').length;
  const activeIncidents = filteredIncidents.filter(i => (i.status || '').toLowerCase() === 'active').length;
  const newIncidents = filteredIncidents.filter(i => (i.status || '').toLowerCase() === 'new').length;
  const resolvedIncidents = filteredIncidents.filter(i => i.status?.toLowerCase() === 'closed').length;

  const handleRefresh = useCallback(() => { refresh(true); }, [refresh]);

  function handleIncidentAction(id: string, action: 'close' | 'assign' | 'status' | 'severity' | 'sync', payload?: ClosePayload | IncidentStatusPayload | IncidentSeverityPayload | Incident) {
    setIncidents(prev => prev.map(i => {
      if (i.id !== id) return i;
      if (action === 'sync' && payload && 'incidentNumber' in payload) {
        const synced = payload as Incident;
        return { ...i, ...synced };
      }
      if (action === 'close') {
        const closePayload = payload as ClosePayload | undefined;
        return {
          ...i,
          status: 'Closed' as const,
          classification: closePayload?.classification ?? i.classification,
          classificationComment: closePayload?.classificationComment ?? i.classificationComment,
          lastUpdateTime: new Date().toISOString(),
        };
      }
      if (action === 'assign' && payload && 'assignee' in payload && payload.assignee) return { ...i, assignedTo: payload.assignee, lastUpdateTime: new Date().toISOString() };
      if (action === 'status' && payload && 'status' in payload) return {
        ...i,
        status: payload.status,
        classification: undefined,
        determination: undefined,
        lastUpdateTime: new Date().toISOString(),
      };
      if (action === 'severity' && payload && 'severity' in payload) return {
        ...i,
        severity: payload.severity,
        lastUpdateTime: new Date().toISOString(),
      };
      return i;
    }));
  }

  function handleFilterSeverity(s: string) { setSeverityFilter(s); setActiveTab('incidents'); }
  function handleFilterStatus(s: string) { setStatusFilter([s]); setActiveTab('incidents'); }
  function handleCreateCase(c: Omit<Case, 'id' | 'createdAt'>) {
    setCases(prev => [...prev, { ...c, id: Date.now(), createdAt: new Date().toISOString() }]);
  }

  // Prefer the Sentinel KQL-driven aggregates so the top KPI cards stay in
  // sync with the Response-time widget. Fall back to the in-browser calc
  // when no customer is selected (MSSP "default" workspace) or when the
  // KQL endpoint is unreachable for this customer.
  const triageSummary = useTriageSummary(activeCustomerId, '30d');
  const mttd = triageSummary.mttd ?? calcMTTD(incidents);
  const mttr = triageSummary.mttr ?? calcMTTR(incidents);
  const noiseRatio = calcNoiseRatio(incidents);
  const secureScoreLabel = secureScore ? `${secureScore.percentage}%` : '-';

  const activeTabDef = useMemo(
    () => visibleTabs.find((tab) => tab.id === activeTab) ?? visibleTabs[0] ?? TABS.find((tab) => tab.id === 'overview'),
    [visibleTabs, activeTab],
  );

  return (
    <>
      <EnterpriseShell
        visibleTabs={visibleTabs}
        activeTab={activeTab}
        onNavigate={setActiveTab}
        customerName={activeCustomer?.name}
        header={
          <Header
            darkMode={darkMode}
            onToggleTheme={() => setDarkMode(d => !d)}
            activeFilter={dateFilter}
            onFilterChange={setDateFilter}
            lastRefresh={lastRefresh}
            onRefresh={handleRefresh}
            onOpenSettings={() => setShowSettings(true)}
            hideSettings={!canOpenSettings}
            azureConfigured={azureConfigured}
            dataSource={source}
            isLoading={isLoading}
            customers={customers}
            activeCustomerId={activeCustomerId}
            onCustomerChange={setActiveCustomerId}
            activeCustomerName={activeCustomer?.name}
            activeCustomerColor={activeCustomer?.color}
          />
        }
      >
        <BannerNotifications />

        {customerError && (
          <div className="ks-error-banner ks-error-banner-warning">
            <span className="font-bold">!</span>
            <span><strong>{activeCustomer?.name ?? 'Customer'}</strong>: {customerError}</span>
          </div>
        )}
        {liveError && !customerError && (
          <div className="ks-error-banner ks-error-banner-warning">
            <span className="font-bold">!</span><span>{liveError}</span>
          </div>
        )}

        {activeTab === 'mission_control' && (
          <MissionControlTab
            customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined}
            customerName={activeCustomer?.name}
            onNavigate={(tabId, targetCustomerId) => {
              if (targetCustomerId) setActiveCustomerId(targetCustomerId);
              setActiveTab(tabId);
            }}
          />
        )}

        {activeTab === 'forward_operating_base' && <ForwardOperatingBaseTab />}

        {activeTab === 'main_operating_base' && (
          <MainOperatingBaseTab
            featureFlags={featureFlags}
            onNavigate={(tabId, targetCustomerId) => {
              if (targetCustomerId) setActiveCustomerId(targetCustomerId);
              setActiveTab(tabId);
            }}
          />
        )}

        {activeTab === 'ai_command_control' && (
          <AiCommandControlCenterTab
            onNavigate={(tabId, targetCustomerId) => {
              if (targetCustomerId) setActiveCustomerId(targetCustomerId);
              setActiveTab(tabId);
            }}
          />
        )}


        {activeTab === 'ml_insights' && (
          <MlInsightsEngineTab
            customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined}
            customerName={activeCustomer?.name}
            onNavigate={(tabId, targetCustomerId) => {
              if (targetCustomerId) setActiveCustomerId(targetCustomerId);
              setActiveTab(tabId);
            }}
          />
        )}


        {activeTab === 'simulation_lab' && (
          <IncidentOpsAiSimulationLabTab
            customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined}
            customerName={activeCustomer?.name}
            onNavigate={(tabId, targetCustomerId) => {
              if (targetCustomerId) setActiveCustomerId(targetCustomerId);
              setActiveTab(tabId);
            }}
          />
        )}

        {activeTab === 'mssp' && <MsspOverviewTab dateFilter={dateFilter} />}

        {activeTab === 'overview' && (
          <div className="slide-in">
            <KpiGrid
              secureScore={secureScoreLabel}
              highIncidents={highIncidents}
              totalIncidents={filteredIncidents.length}
              activeIncidents={activeIncidents + newIncidents}
              resolvedIncidents={resolvedIncidents}
              onFilterSeverity={handleFilterSeverity}
              onFilterStatus={handleFilterStatus}
              dateFilter={dateFilter}
            />
            <ChartsSection dailyAlerts={dailyAlerts} incidents={filteredIncidents} darkMode={darkMode} />
            {activeCustomerId !== 'default' && (
              <div className="mb-4">
                <MttrWidget customerId={activeCustomerId} defaultRange="30d" />
              </div>
            )}
            <ThreatIntelSection articles={threatArticles} mttd={mttd} mttr={mttr} noiseRatio={noiseRatio} incidents={filteredIncidents} customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} />
            <CasesPanel cases={cases} onCreateCase={handleCreateCase} onUpdateCase={() => {}} />
            <IncidentsTab
              incidents={filteredIncidents.slice(0, 10)}
              severityFilter={severityFilter}
              statusFilter={statusFilter}
              onSeverityFilter={handleFilterSeverity}
              onStatusFilter={handleFilterStatus}
              onIncidentAction={handleIncidentAction}
              customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined}
              onRunKql={runKqlInConsole}
              readOnly={!canWrite('incidents', activeCustomerId !== 'default' ? activeCustomerId : undefined)}
            />
          </div>
        )}

        {activeTab === 'incidents' && (
          <IncidentsTab
            incidents={filteredIncidents}
            severityFilter={severityFilter}
            statusFilter={statusFilter}
            onSeverityFilter={setSeverityFilter}
            onStatusFilter={setStatusFilter}
            onIncidentAction={handleIncidentAction}
            customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined}
            onRunKql={runKqlInConsole}
            autoOpenIncidentNumber={autoOpenIncident}
            onAutoOpenConsumed={() => setAutoOpenIncident(null)}
            showGuardianOperations={featureFlags['guardian_operations_stage5d'] !== false}
            readOnly={!canWrite('incidents', activeCustomerId !== 'default' ? activeCustomerId : undefined)}
          />
        )}

        {activeTab === 'datalake' && (
          <DataLakeTab
            customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined}
            customerName={activeCustomer?.name}
            pendingQuery={pendingKql}
            onPendingQueryConsumed={() => setPendingKql(null)}
          />
        )}
        {activeTab === 'hunts' && <HuntsTab />}
        {activeTab === 'workbooks' && <WorkbooksTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} />}
        {activeTab === 'notebook_ops' && <NotebookOpsTab />}
        {activeTab === 'responseops' && <ResponseOpsPlaybookStudioTab />}
        {activeTab === 'risk_command_center' && <RiskCommandCenterTab />}
        {activeTab === 'advanced_hunting' && (
          <AdvancedHuntingTab
            featureFlags={featureFlags}
            pendingQuery={pendingKql}
            onPendingQueryConsumed={() => setPendingKql(null)}
          />
        )}
        {activeTab === 'phishing' && <PhishingTab />}
        {activeTab === 'darkweb' && <DarkWebTab />}
        {activeTab === 'spiderfoot' && <SpiderFootTab />}
        {activeTab === 'dnstwist' && <DnstwistTab />}
        {activeTab === 'coverage' && <CoverageTab />}
        {activeTab === 'oob_coverage_assessment' && <OobCoverageAssessmentTab />}
        {activeTab === 'custom_analytics_studio' && <CustomAnalyticsStudioTab />}
        {activeTab === 'vulnerability_mgmt' && <VulnerabilityManagementTab />}
        {activeTab === 'entity_360' && <HuntsTab />}
        {activeTab === 'soc_digital_twin' && <SocDigitalTwinTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} />}
        {activeTab === 'soc_debt' && <SocDebtDashboardTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} />}
        {activeTab === 'report_builder' && <AiReportBuilderTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} />}
        {activeTab === 'connector_health' && <ConnectorHealthGuardianTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} />}
        {activeTab === 'detection_quality' && <DetectionQualityScoreTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} />}
        {activeTab === 'customer_exec' && <SocKpiTab customerId={activeCustomerId !== 'default' ? activeCustomerId : undefined} customerName={activeCustomer?.name} darkMode={darkMode} />}
        {/* Threat Intelligence: visible to admin + every SOC tier.
            IOCUploadTab itself toggles its admin-only "Upload IOCs" form
            internally based on the current role; non-admins see the
            reputation-lookup view. The /ioc/upload endpoint also re-checks
            admin server-side. */}
        {activeTab === 'ioc' && user && <IOCUploadTab onRunKql={runKqlInConsole} />}
        {activeTab === 'security_copilot' && <SecurityCopilotTab />}
        {activeTab === 'reports' && <ReportsTab />}
        {activeTab === 'log_master' && <LogMasterTab />}
        {activeTab === 'masters' && <MastersTab />}
        {activeTab === 'logs' && <LogsTab />}
      </EnterpriseShell>

      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
    </>
  );
}
